/**
 * Référentiel des fluides tenu par MyEquip (décision 11 du 19/10/2026) et
 * périodicité réglementaire des contrôles d'étanchéité.
 *
 * Le référentiel est semé dans chaque entreprise (migration 0192, puis à la
 * création d'une entreprise) ; une entreprise peut ajouter un fluide absent,
 * qui reste « à vérifier ». Le PRP d'une machine liée à un fluide du
 * référentiel est celui du référentiel : une seule source.
 *
 * PRP (potentiel de réchauffement planétaire, GWP100) : colonne « F-gas
 * Regulation » du tableau de l'Umweltbundesamt (mars 2026), qui reprend
 * l'annexe I (HFC, AR4), l'annexe II (HFO, autres gaz) et le calcul de
 * l'annexe VI (mélanges) du règlement (UE) 2024/573. Les fluides absents du
 * règlement (HCFC, propène) gardent la valeur du 4e rapport du GIEC (AR4).
 * Valeurs arrondies à l'unité : la colonne est entière, et sous 1 le PRP
 * d'un HFO ne compte pas pour ses seuils, fixés en kilogrammes.
 */
import { cerfaThresholds, classifyFluid, fluidNumber, type CerfaFluidFamily } from './cerfa.js';

/** Version du référentiel : à changer avec toute migration qui le modifie. */
export const FLUID_REFERENCE_VERSION = '2026-10-20';

export type FluidGwpSource = 'ue_2024_573' | 'giec_ar4';

/** Catégorie d'un fluide : famille du formulaire CERFA, ou fluide non fluoré. */
export const FLUID_CATEGORIES = ['HFC', 'HFO', 'HCFC', 'CFC', 'naturel'] as const;
export type FluidCategory = (typeof FLUID_CATEGORIES)[number];

export interface FluidReferenceEntry {
  /** Désignation ASHRAE 34 telle qu'affichée. */
  code: string;
  gwp: number;
  source: FluidGwpSource;
}

export const FLUID_REFERENCE: readonly FluidReferenceEntry[] = [
  // HFC purs (annexe I, section 1)
  { code: 'R-23', gwp: 14800, source: 'ue_2024_573' },
  { code: 'R-32', gwp: 675, source: 'ue_2024_573' },
  { code: 'R-125', gwp: 3500, source: 'ue_2024_573' },
  { code: 'R-134a', gwp: 1430, source: 'ue_2024_573' },
  { code: 'R-143a', gwp: 4470, source: 'ue_2024_573' },
  { code: 'R-152a', gwp: 124, source: 'ue_2024_573' },
  { code: 'R-227ea', gwp: 3220, source: 'ue_2024_573' },
  { code: 'R-245fa', gwp: 1030, source: 'ue_2024_573' },
  // Mélanges (annexe VI)
  { code: 'R-404A', gwp: 3922, source: 'ue_2024_573' },
  { code: 'R-407A', gwp: 2107, source: 'ue_2024_573' },
  { code: 'R-407C', gwp: 1774, source: 'ue_2024_573' },
  { code: 'R-407F', gwp: 1825, source: 'ue_2024_573' },
  { code: 'R-407H', gwp: 1495, source: 'ue_2024_573' },
  { code: 'R-410A', gwp: 2088, source: 'ue_2024_573' },
  { code: 'R-413A', gwp: 2095, source: 'ue_2024_573' },
  { code: 'R-417A', gwp: 2346, source: 'ue_2024_573' },
  { code: 'R-422A', gwp: 3143, source: 'ue_2024_573' },
  { code: 'R-422D', gwp: 2729, source: 'ue_2024_573' },
  { code: 'R-427A', gwp: 2138, source: 'ue_2024_573' },
  { code: 'R-434A', gwp: 3245, source: 'ue_2024_573' },
  { code: 'R-437A', gwp: 1805, source: 'ue_2024_573' },
  { code: 'R-438A', gwp: 2264, source: 'ue_2024_573' },
  { code: 'R-442A', gwp: 1888, source: 'ue_2024_573' },
  { code: 'R-444B', gwp: 293, source: 'ue_2024_573' },
  { code: 'R-448A', gwp: 1386, source: 'ue_2024_573' },
  { code: 'R-449A', gwp: 1396, source: 'ue_2024_573' },
  { code: 'R-449B', gwp: 1411, source: 'ue_2024_573' },
  { code: 'R-450A', gwp: 601, source: 'ue_2024_573' },
  { code: 'R-452A', gwp: 2139, source: 'ue_2024_573' },
  { code: 'R-452B', gwp: 697, source: 'ue_2024_573' },
  { code: 'R-454A', gwp: 237, source: 'ue_2024_573' },
  { code: 'R-454B', gwp: 465, source: 'ue_2024_573' },
  { code: 'R-454C', gwp: 146, source: 'ue_2024_573' },
  { code: 'R-455A', gwp: 146, source: 'ue_2024_573' },
  { code: 'R-507A', gwp: 3985, source: 'ue_2024_573' },
  { code: 'R-508B', gwp: 13504, source: 'ue_2024_573' },
  { code: 'R-513A', gwp: 629, source: 'ue_2024_573' },
  { code: 'R-515B', gwp: 288, source: 'ue_2024_573' },
  // HFO et HCFO (annexe II, section 1)
  { code: 'R-1234yf', gwp: 1, source: 'ue_2024_573' },
  { code: 'R-1234ze(E)', gwp: 1, source: 'ue_2024_573' },
  { code: 'R-1233zd(E)', gwp: 4, source: 'ue_2024_573' },
  { code: 'R-1336mzz(Z)', gwp: 2, source: 'ue_2024_573' },
  // Fluides non fluorés (annexe II, section 4)
  { code: 'R-290', gwp: 0, source: 'ue_2024_573' },
  { code: 'R-600a', gwp: 0, source: 'ue_2024_573' },
  { code: 'R-717', gwp: 0, source: 'ue_2024_573' },
  { code: 'R-744', gwp: 1, source: 'ue_2024_573' },
  // Hors du règlement : 4e rapport du GIEC
  { code: 'R-1270', gwp: 2, source: 'giec_ar4' },
  { code: 'R-22', gwp: 1810, source: 'giec_ar4' },
];

/** Catégorie d'un fluide d'après sa désignation, `null` si elle n'est pas connue. */
export function fluidCategory(code: string): FluidCategory | null {
  const { scope, family } = classifyFluid(code);
  if (scope === 'hors_champ') return 'naturel';
  return family;
}

/** Clé de comparaison de deux désignations (« r404a » = « R-404A ») ; même règle que `app_private.fluid_key`. */
export function fluidKey(code: string | null | undefined): string {
  return fluidNumber(code);
}

const REFERENCE_BY_KEY = new Map(FLUID_REFERENCE.map((entry) => [fluidKey(entry.code), entry]));

/** Entrée du référentiel pour une désignation, quelle que soit son écriture. */
export function referenceFluid(code: string | null | undefined): FluidReferenceEntry | undefined {
  return REFERENCE_BY_KEY.get(fluidKey(code));
}

/**
 * Base de la périodicité d'une machine :
 * - `reglementaire` : seuil atteint, périodicité de l'article 5 du règlement ;
 * - `sous_seuil` : fluide fluoré sous le premier seuil, aucun contrôle obligatoire ;
 * - `hors_champ` : pas de fluide, ou fluide non fluoré ;
 * - `a_verifier` : famille, PRP ou charge inconnus, ou fluide ajouté par
 *   l'entreprise : 12 mois au plus par prudence, jusqu'à vérification.
 */
export type LeakCheckBasis = 'reglementaire' | 'sous_seuil' | 'hors_champ' | 'a_verifier';

export interface LeakCheckPeriodicity {
  basis: LeakCheckBasis;
  /** Périodicité légale ou de prudence, `null` sans obligation. */
  legalMonths: number | null;
  /** Périodicité appliquée : la saisie manuelle ne peut que la raccourcir. */
  months: number | null;
  family: CerfaFluidFamily | null;
  /** Tonnes équivalent CO₂ calculées avec le PRP, `null` sans charge ou sans PRP. */
  teqCo2: number | null;
}

/** Périodicité de prudence quand rien ne permet de conclure. */
export const LEAK_CHECK_PRECAUTION_MONTHS = 12;

export function leakCheckPeriodicity(input: {
  fluidCode: string | null;
  /** Catégorie déclarée au référentiel de l'entreprise. */
  declaredCategory?: string | null;
  /** Fluide ajouté par l'entreprise, pas encore vérifié. */
  toVerify?: boolean;
  chargeGrams: number | null;
  gwp: number | null;
  /** Détection permanente : inconnue, elle compte comme absente (le plus prudent). */
  permanentDetection: boolean | null;
  manualMonths?: number | null;
}): LeakCheckPeriodicity {
  const code = input.fluidCode?.trim() ?? '';
  const { scope, family } = code ? classifyFluid(code, input.declaredCategory) : { scope: 'hors_champ' as const, family: null };
  const chargeKg = input.chargeGrams !== null && input.chargeGrams > 0 ? input.chargeGrams / 1000 : null;
  const gwp = input.gwp !== null && input.gwp > 0 ? input.gwp : null;
  const teqCo2 = chargeKg !== null && gwp !== null ? (chargeKg * gwp) / 1000 : null;
  const precaution = { basis: 'a_verifier' as const, legalMonths: LEAK_CHECK_PRECAUTION_MONTHS };
  let legal: { basis: LeakCheckBasis; legalMonths: number | null };
  if (scope === 'hors_champ') {
    legal = code && input.toVerify ? precaution : { basis: 'hors_champ', legalMonths: null };
  } else if (scope === 'inconnu' || family === null || family === 'CFC' || chargeKg === null
    || (family === 'HFC' && teqCo2 === null)) {
    legal = precaution;
  } else {
    const { frequencyMonths } = cerfaThresholds({
      family, chargeKg, teqCo2, permanentDetection: input.permanentDetection ?? false,
    });
    if (frequencyMonths === null) legal = input.toVerify ? precaution : { basis: 'sous_seuil', legalMonths: null };
    else legal = { basis: input.toVerify ? 'a_verifier' : 'reglementaire', legalMonths: frequencyMonths };
  }
  const manual = input.manualMonths && input.manualMonths > 0 ? input.manualMonths : null;
  const months = legal.legalMonths === null ? manual : Math.min(manual ?? legal.legalMonths, legal.legalMonths);
  return { ...legal, months, family, teqCo2 };
}
